import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useCanvasStore } from '@/stores/canvasStore';
import { usePartnrStore } from '@/stores/partnrStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';
import { isPartnrV2Enabled } from '@/lib/partnr/flag';
import { PartnrMark } from '@/components/partnr/kit/PartnrMark';
import { CanvasControlGroup } from '@/components/canvas/CanvasControl';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { DockGlyph } from '@/components/toolbar/DockGlyph';
import { modKey } from '@/components/partnr/panel/shortcuts';

/** The panel's own slide curve and length, so the launcher rides its edge. */
const PANEL_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const PANEL_SLIDE_MS = 260;
const SPRING = { type: 'spring', stiffness: 500, damping: 38 } as const;

/**
 * The one control on the canvas's right edge: opens and closes Partnr.
 *
 * Opening, the docked panel slides in from the right and the launcher glides
 * left on the same curve, so it reads as the panel pushing it along. While a
 * chat is open the dark fill blooms out from the mark; while Partnr is working
 * the mark drifts. On a bottom sheet (narrow screens) the panel covers no
 * canvas on the right, so the launcher stays put.
 */
export function PartnrLauncher() {
  const viewMode = useCanvasStore((s) => s.viewMode);
  const currentBoardId = useCanvasStore((s) => s.currentBoardId);
  const isPartnrBetaEnabled = usePartnrBetaStore((s) => s.isPartnrBetaEnabled);
  const [v2] = useState(isPartnrV2Enabled);

  const panelOpen = usePartnrPanelStore((s) => s.isOpen);
  const rightInset = usePartnrPanelStore((s) => s.rightInset);
  const panelWorking = usePartnrPanelStore((s) => s.liveMessage !== null);
  const v1Open = usePartnrStore((s) => s.activeChats.length > 0);
  const v1Working = usePartnrStore((s) => s.isStreaming);
  const still = useReducedMotion();

  if (!isPartnrBetaEnabled || viewMode !== 'focus' || !currentBoardId) return null;

  const open = v2 ? panelOpen : v1Open;
  const working = v2 ? panelWorking : v1Working;
  const label = open ? 'Close Partnr' : 'Open Partnr';

  const handleClick = () => {
    if (v2) {
      usePartnrPanelStore.getState().toggle();
      return;
    }
    // Partnr 1 escape hatch: a click opens a fresh chat, the window closes itself
    if (!v1Open) void usePartnrStore.getState().createChat(currentBoardId, []);
  };

  return (
    <CanvasControlGroup
      className="fixed top-[4.5rem] right-4 z-40"
      style={{
        transform: rightInset ? `translateX(${-rightInset}px)` : undefined,
        transition: still ? undefined : `transform ${PANEL_SLIDE_MS}ms ${PANEL_EASE}`,
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            type="button"
            aria-label={label}
            aria-pressed={open}
            data-partnr-launcher
            data-dock-item
            onClick={handleClick}
            whileTap={still ? undefined : { scale: 0.9 }}
            transition={SPRING}
            className={cn(
              'relative flex h-10 w-10 items-center justify-center rounded-full',
              'transition-colors duration-150 ease-out motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal',
              open ? 'text-primary-foreground' : 'text-foreground hover:bg-muted/60',
            )}
          >
            <AnimatePresence initial={false}>
              {open && (
                <motion.span
                  key="fill"
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-primary shadow-md"
                  initial={still ? { opacity: 0 } : { scale: 0.4, opacity: 0 }}
                  animate={still ? { opacity: 1 } : { scale: 1, opacity: 1 }}
                  exit={still ? { opacity: 0 } : { scale: 0.4, opacity: 0 }}
                  transition={still ? { duration: 0.12 } : SPRING}
                />
              )}
            </AnimatePresence>
            {/* Magnifies on hover like every canvas control; the fill stays put */}
            <DockGlyph>
              <motion.span
                className="relative flex"
                animate={still ? undefined : { rotate: open ? 180 : 0 }}
                transition={SPRING}
              >
                <PartnrMark size={20} state={working ? 'thinking' : 'idle'} />
              </motion.span>
            </DockGlyph>
          </motion.button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>
            {label}
            {v2 && <span className="ml-2 text-muted-foreground">{modKey()}J</span>}
          </p>
        </TooltipContent>
      </Tooltip>
    </CanvasControlGroup>
  );
}
